import { motion } from "framer-motion";
import { ArrowDown, CheckCircle, CircleNotch, DownloadSimple } from "@phosphor-icons/react";
import { useDownload } from "@/lib/download";
import { scrollToSection } from "./Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1625014618427-fbc980b974f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMDNkJTIwY3J5cHRvJTIwY29pbiUyMGRhcmt8ZW58MHx8fHwxNzg3NTc0MTAxfDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { startDownload, downloading } = useDownload();

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      <div
        aria-hidden
        className="absolute -top-40 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(0,245,160,0.12),transparent_65%)]"
      />
      <div
        aria-hidden
        className="absolute bottom-[-20%] left-[-15%] h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,rgba(37,99,235,0.14),transparent_65%)]"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#00F5A0]/25 bg-[#00F5A0]/5 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-[#00F5A0]"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#00F5A0]" />
            USDT → INR Exchange · India
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-slate-50 sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Convert USDT to INR</MaskedLine>
            <MaskedLine delay={0.3}>
              at the <span className="text-[#00F5A0]">Best Rate</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subtext"
          >
            Get the best USDT to INR exchange rates with Coin Adda. Download our
            app, deposit your USDT and receive INR securely — straight to your
            bank account.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              type="button"
              data-testid="hero-download-btn"
              onClick={startDownload}
              disabled={downloading}
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#00F5A0] px-8 py-4 text-base font-bold text-[#050B14] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-[#00D68B] hover:shadow-[0_16px_50px_rgba(0,245,160,0.35)] disabled:opacity-70"
            >
              {downloading ? (
                <CircleNotch size={20} weight="bold" className="animate-spin" />
              ) : (
                <DownloadSimple size={20} weight="bold" />
              )}
              {downloading ? "Starting Download…" : "Download Coin Adda App"}
            </button>
            <button
              type="button"
              data-testid="hero-start-exchange-btn"
              onClick={() => scrollToSection("#app-download")}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-slate-100 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#00F5A0]/50 hover:bg-white/5"
            >
              Start Exchange
              <ArrowDown size={18} weight="bold" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500"
            data-testid="hero-trust-chips"
          >
            <span>Fast Processing</span>
            <span>Secure Transactions</span>
            <span>Dedicated Support</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <div
            aria-hidden
            className="absolute inset-0 -z-10 translate-x-6 translate-y-6 rounded-[2rem] bg-[radial-gradient(circle_at_center,rgba(0,245,160,0.18),transparent_70%)] blur-2xl"
          />
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
          >
            <img
              src={HERO_IMG}
              alt="USDT to INR exchange visual"
              className="aspect-[4/5] w-full object-cover sm:aspect-square"
              data-testid="hero-visual"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            className="absolute -left-4 top-8 rounded-2xl border border-white/10 bg-[#0F172A]/85 px-5 py-4 backdrop-blur-xl sm:-left-8"
            data-testid="hero-rate-chip"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Best Rate</p>
            <p className="mt-1 font-mono text-lg font-bold text-[#00F5A0]">1 USDT → ₹ INR</p>
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
            className="absolute -right-3 bottom-10 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0F172A]/85 px-5 py-4 backdrop-blur-xl sm:-right-6"
            data-testid="hero-payout-chip"
          >
            <CheckCircle size={26} weight="duotone" className="text-[#00F5A0]" />
            <div>
              <p className="text-sm font-semibold text-slate-100">INR Payout</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Bank Withdrawal</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
